(function () {
    'use strict';

    /**
     * Find the main product form. The Shop Pay installments form also posts
     * to /cart/add and can appear earlier in the DOM, so prefer the form
     * with the main-product-form class and scope to this product's section.
     *
     * @returns {Element|null}
     */
    function getForm() {
        return productInfo.querySelector('form.main-product-form')
            || productInfo.querySelector('form[action*="/cart/add"]')
            || document.querySelector('form[action*="/cart/add"]');
    }

    /**
     * Find the express checkout element. This theme renders Shopify's
     * accelerated checkout web component (<shopify-accelerated-checkout>,
     * which contains the Shop Pay button + "More payment options" inside
     * closed shadow DOM); older themes use .shopify-payment-button.
     *
     * @returns {Element|null}
     */
    function getExpressButton() {
        return productInfo.querySelector('shopify-accelerated-checkout')
            || productInfo.querySelector('.shopify-payment-button')
            || (form ? form.querySelector('shopify-accelerated-checkout, .shopify-payment-button') : null);
    }

    /**
     * Get the currently selected product variant's ID.
     *
     * @returns {string|null}
     */
    function getCurrentVariantId() {
        const idInput = form ? form.querySelector('input[name="id"]') : null;

        return idInput instanceof Element ? idInput.value : null;
    }

    /**
     * Partner details fields are required when any option
     * is exactly "2" or includes "couple" (case-insensitive).
     *
     * @returns {boolean}
     */
    function isPartnerRequired() {
        const variantId = getCurrentVariantId();
        if (!variantId) return false;

        const selectedOptions = variantOptions[variantId];
        if (!Array.isArray(selectedOptions)) return false;

        let required = false;
        selectedOptions.forEach(function (opt) {
            if (required) return;
            const s = String(opt).toLowerCase().trim();
            if (!s) return;
            if (s === '2' || s.indexOf('couple') !== -1) required = true;
        });

        return required;
    }

    /**
     * Toggle whether partner details fields are visible. Clears any error
     * state when the variant no longer requires them.
     *
     * @returns {void}
     */
    function updatePartnerFieldsVisibility() {
        if (isPartnerRequired()) {
            wrapper.style.display = 'block';
            wrapper.hidden = false;
            inputName.required = true;
            inputEmail.required = true;
        } else {
            wrapper.style.display = 'none';
            wrapper.hidden = true;
            inputName.required = false;
            inputEmail.required = false;
            inputName.value = '';
            inputEmail.value = '';
            if (inputName.setCustomValidity) inputName.setCustomValidity('');
            if (inputEmail.setCustomValidity) inputEmail.setCustomValidity('');
            clearPartnerErrors();
        }
    }

    /**
     * Validate partner's email.
     *
     * @returns {boolean}
     */
    function validatePartnerEmail() {
        if (!isPartnerRequired()) return true;

        const email = (inputEmail.value || '').trim();
        if (!email) return false;

        return inputEmail.checkValidity();
    }

    /**
     * Validate partner's name.
     *
     * @returns {boolean}
     */
    function validatePartnerName() {
        if (!isPartnerRequired()) return true;

        return (inputName.value || '').trim().length > 0;
    }

    /**
     * Whether partner details are complete enough to proceed to checkout.
     * Always true when the selected variant doesn't require them.
     *
     * @returns {boolean}
     */
    function partnerDetailsValid() {
        if (!isPartnerRequired()) return true;

        return validatePartnerName() && validatePartnerEmail();
    }

    /**
     * Show or hide the inline prompt. Created lazily (only when first needed)
     * and anchored just above the express checkout button, so nothing appears
     * by default.
     *
     * @param {boolean} show
     * @returns {void}
     */
    function showPartnerMessage(show) {
        let msg = document.getElementById('partner-gate-message');

        if (!show) {
            if (msg) msg.style.display = 'none';
            return;
        }

        if (!msg) {
            const anchor = getExpressButton();
            if (!anchor || !anchor.parentNode) return;
            msg = document.createElement('p');
            msg.id = 'partner-gate-message';
            msg.className = 'input-hint partner-gate-message';
            msg.setAttribute('role', 'alert');
            msg.setAttribute('aria-live', 'polite');
            msg.textContent = 'Please add your partner\'s name and email to continue.';
            anchor.parentNode.insertBefore(msg, anchor);
        }

        msg.style.display = 'block';
    }

    /**
     * Mark or unmark a field as invalid — visual highlight plus the ARIA
     * hooks (aria-invalid + aria-describedby pointing at the inline prompt).
     *
     * @param {Element} input
     * @param {boolean} invalid
     * @returns {void}
     */
    function setFieldInvalid(input, invalid) {
        input.classList.toggle('field--error', invalid);
        if (invalid) {
            input.setAttribute('aria-invalid', 'true');
            input.setAttribute('aria-describedby', 'partner-gate-message');
        } else {
            input.removeAttribute('aria-invalid');
            input.removeAttribute('aria-describedby');
        }
    }

    /**
     * Flag missing/invalid details: highlight the empty field(s), show the
     * inline prompt, and focus the first one. Called only on a failed attempt
     * to check out — never by default.
     *
     * @returns {void}
     */
    function flagMissingPartnerDetails() {
        const nameOk = validatePartnerName();
        const emailOk = validatePartnerEmail();

        setFieldInvalid(inputName, !nameOk);
        setFieldInvalid(inputEmail, !emailOk);

        showPartnerMessage(true);

        if (!nameOk) inputName.focus();
        else if (!emailOk) inputEmail.focus();
    }

    /**
     * Clear the inline prompt and field highlights.
     *
     * @returns {void}
     */
    function clearPartnerErrors() {
        setFieldInvalid(inputName, false);
        setFieldInvalid(inputEmail, false);
        showPartnerMessage(false);
    }

    /**
     * Take over the express checkout button on the couple/"2" variant.
     * Native Shop Pay builds its checkout on Shopify's servers from the
     * variant alone and can't carry custom line-item properties, so we
     * intercept the click (capture phase, before the component's own
     * handlers inside its closed shadow DOM). If details are missing we flag
     * them; if valid we route the purchase through the cart — which reliably
     * carries the partner properties onto the order, like the Add-to-Cart
     * flow Genie already handles. Pointer/click events are composed, so they
     * cross the shadow boundary and reach document first in the capture phase.
     *
     * @param {Event} e
     * @returns {void}
     */
    function expressClickGuard(e) {
        if (!isPartnerRequired()) return;

        const expressBtn = getExpressButton();
        if (!expressBtn) return;
        if (!(e.target === expressBtn || expressBtn.contains(e.target))) return;

        // Neutralise the native Shop Pay handlers. Don't preventDefault on
        // the pointer events (that can cancel the click a tap synthesises);
        // only stop propagation. The click itself is fully cancelled below.
        e.stopImmediatePropagation();

        if (e.type !== 'click') return;

        e.preventDefault();

        if (!partnerDetailsValid()) {
            flagMissingPartnerDetails();
            return;
        }

        proceedThroughCart();
    }

    // Guard against a double-tap firing two /cart/add.js posts before the redirect.
    let checkoutInFlight = false;

    /**
     * Add the bundle to the cart with the partner line-item properties, then
     * send the buyer to checkout (where Shop Pay is still offered). This is
     * the supported way to get custom properties onto an express order.
     *
     * @returns {void}
     */
    function proceedThroughCart() {
        if (checkoutInFlight) return;
        checkoutInFlight = true;

        const variantId = getCurrentVariantId();
        if (!variantId) {
            checkoutInFlight = false;
            window.location.href = '/cart';
            return;
        }

        const qtyInput = form ? form.querySelector('input[name="quantity"]') : null;
        const quantity = (qtyInput && qtyInput.value) ? qtyInput.value : '1';

        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', quantity);
        body.append('properties[Partner name]', (inputName.value || '').trim());
        body.append('properties[Partner email]', (inputEmail.value || '').trim());

        // Use the original (un-wrapped) fetch so we don't double-inject via
        // the /cart/add interceptor below — the properties are already here.
        originalFetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            body: body
        }).then(function (res) {
            if (!res.ok) throw new Error('Add to cart failed (' + res.status + ')');
            window.location.href = '/checkout';
        }).catch(function () {
            // If the add fails, release the lock and fall back to the cart.
            checkoutInFlight = false;
            window.location.href = '/cart';
        });
    }

    /**
     * Append partner details into the line item's custom properties via the form data.
     *
     * @param {FormData} body
     * @param {string} nameValue
     * @param {string} emailValue
     *
     * @returns {FormData}
     */
    function appendPartnerDetailsToFormData(body, nameValue, emailValue) {
        if (! body instanceof FormData) {
            throw new Error('Expected FormData, got ' + typeof body);
        }

        if (nameValue)  body.append('properties[Partner name]', nameValue);
        if (emailValue) body.append('properties[Partner email]', emailValue);

        return body;
    }

    /**
     * Inject partner details into the `/cart/add` payload (the Add-to-Cart
     * path). If details are missing, flag them inline and block the add.
     *
     * @param {string|null} url
     * @param options
     * @returns {Promise<Response>|Promise<never>}
     */
    function injectPartnerDetailsIntoHttpRequest(url, options) {
        const shouldInject = url
            && typeof url === 'string'
            && url.indexOf('/cart/add') !== -1
            && isPartnerRequired();

        if (shouldInject) {
            if (!partnerDetailsValid()) {
                flagMissingPartnerDetails();
                return Promise.reject(new Error('Invalid partner details'));
            }

            const nameValue  = (inputName.value  || '').trim();
            const emailValue = (inputEmail.value || '').trim();

            if (options && options.body && (nameValue || emailValue)) {
                options.body = appendPartnerDetailsToFormData(options.body, nameValue, emailValue);
            }
        }

        return originalFetch.apply(this, arguments);
    }

    // Find the inputs, the product section container, and the form.
    const wrapper = document.getElementById('partner-details-wrapper');
    const inputName = document.getElementById('partner-name');
    const inputEmail = document.getElementById('partner-email');
    // Scope lookups to this product's section so we don't touch other
    // product instances on the page (e.g. quick-view drawers).
    const productInfo = (wrapper && wrapper.closest('[id^="ProductInfo"]')) || document;
    const form = getForm();
    // Variant option map is passed from Liquid via a data- attribute on the
    // wrapper, since an asset_url'd file can't run Liquid, so the data rides the DOM.
    const variantOptions = JSON.parse((wrapper && wrapper.dataset.variantOptions) || '{}');

    // Early return if we can't find the form or its inputs.
    if (!form || !wrapper || !inputName || !inputEmail) return;

    // Initial state (shows/hides fields; never flags anything).
    updatePartnerFieldsVisibility();

    // As the customer types, clear a field's error once it's valid, and hide
    // the prompt entirely once both fields are good.
    [inputName, inputEmail].forEach(function (input) {
        input.addEventListener('input', function () {
            if (!this.value || this.checkValidity()) {
                setFieldInvalid(this, false);
            }
            if (partnerDetailsValid()) {
                showPartnerMessage(false);
            }
        });
    });

    // React to product variant changes
    const shopifyEvents = ['change', 'variant:change', 'product:variant-change', 'variant-change'];
    shopifyEvents.forEach((event) => document.addEventListener(event, updatePartnerFieldsVisibility));

    // Intercept express checkout (capture phase, earliest point) so we can
    // flag missing details or route the purchase through the cart.
    ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'click'].forEach(function (ev) {
        document.addEventListener(ev, expressClickGuard, true);
    });

    // Intercept the Add-to-Cart request and append partner details.
    const originalFetch = window.fetch;
    if (originalFetch) window.fetch = injectPartnerDetailsIntoHttpRequest;
})();
